// A boolean callback contract shared by App and API-failure probes. Unavailability
// retains the last known state; it is not evidence that maintenance has ended.
export function createMaintenanceStatus({ read, storage, now = Date.now, ttl = 5 * 60 * 1000 }) {
  let cached;
  let checkedAt = -Infinity;
  let inFlight;
  let observation = 0;
  const stored = () => {
    try {
      return storage.getItem('isMaintenance') === 'true';
    } catch (_) {
      return false;
    }
  };
  const check = async ({ cb, updateLocalStorage = false } = {}) => {
    if (cached === undefined || now() - checkedAt >= ttl) {
      if (!inFlight) {
        const startedObservation = observation;
        inFlight = Promise.resolve()
          .then(read)
          .then((value) => {
            if (startedObservation !== observation) return cached;
            if (typeof value !== 'boolean') throw new Error('Invalid maintenance status');
            cached = value;
            checkedAt = now();
            return value;
          })
          .catch(() => {
            cached = cached === undefined ? stored() : cached;
            checkedAt = now();
            return cached;
          })
          .finally(() => {
            inFlight = null;
          });
      }
      const value = await inFlight;
      if (updateLocalStorage) {
        try {
          storage.setItem('isMaintenance', String(value));
        } catch (_) {
          /* Storage is optional. */
        }
      }
      if (cb) cb(value);
      return value;
    }
    if (updateLocalStorage) {
      try {
        storage.setItem('isMaintenance', String(cached));
      } catch (_) {
        /* Storage is optional. */
      }
    }
    if (cb) cb(cached);
    return cached;
  };
  check.observeBroadcast = (value) => {
    if (value !== 'true' && value !== 'false') return;
    observation++;
    cached = value === 'true';
    checkedAt = now();
  };
  return check;
}

export const fetchMaintenanceStatus = createMaintenanceStatus({
  storage: {
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value),
  },
  read: async () => {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 10000);
    try {
      const res = await fetch('https://settings.rezrate.com/config.js', { cache: 'no-store', signal: abort.signal });
      if (!res.ok) throw new Error('Maintenance status unavailable');
      const data = await res.json();
      return process.env.REACT_APP_BUILD_TYPE === 'liveproduction' ? data.isProdMaintenance : data.isDevMaintenance;
    } finally {
      clearTimeout(timeout);
    }
  },
});
